import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import React, { forwardRef, useEffect, useState } from 'react';
import { ITheme, useTheme } from 'src/theme';
import Icon from '../Icon';

const RadioButtonDescription = styled.div<
  Partial<RadioButtonProps> & { isSelected: boolean }
>`
  display: flex;
  flex-direction: column;
  gap: 1rem 0;
  color: ${(props) =>
    props.isSelected && props.activeType === 'fill'
      ? '#FFF'
      : props.isSelected
      ? '#883E89'
      : '#8A8988'};
  text-align: ${(props) => (props.textAlign ? props.textAlign : 'left')};
  h4 {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
  }
  p {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
  }
  @media screen and (max-width: 768px) {
    h4 {
      font-size: 16px;
    }

  }
`;

const RadioButtonSubText = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: 0.015em;
  text-align: center;
  b {
    font-weight: 600;
  }
`;

const RadioButtonGroupContainer = styled.div<Partial<RadioButtonGroupProps>>`
  display: flex;
  flex-direction:column;
  gap:  ${(props) => props.buttonGap ? props.buttonGap : '16px'};
  width: 100%;
  align-items: center;
  @media screen and (min-width: 768px) {
    gap:  ${(props) => props.buttonGap ? props.buttonGap : '0 24px'}; 
    width: auto;
    flex-direction:row;
  }

  ////Overlaping stepper fixes
  // flex-direction:column;
  // gap: 16px;
  // width: 100%;
  // @media screen and (min-width: 768px) {
  //   gap: 0 24px;
  //   width: auto;
  //   flex-direction:row;
  // }
`;

const RadioButtonContainer = styled.button<
  Partial<RadioButtonProps> & { isSelected: boolean }
>`
  display: flex;
  width: ${props => props.width ? props.width + 'px' : '100%'};
  height: ${props => props.height ? props.height + 'px' : '100%'};
  border: none;
  flex-direction: ${(props) =>
    props.iconALign === 'top'
      ? 'column'
      : props.iconALign === 'bottom'
      ? 'column-reverse'
      : props.iconALign === 'left'
      ? 'row'
      : 'row-reverse'};
  align-items: center;
  gap: 12px;
  background-color: ${(props) =>
    props.isSelected && props.activeType === 'fill'
      ? '#883E89'
      : props.isSelected
      ? '#FFF'
      : '#FAFAFA'};
  border-radius:${(props) => props.borderRadius ? props.borderRadius : '32px'};
  cursor: pointer;
  box-shadow: ${(props) =>
    props.isSelected ? '0px 1px 20px rgba(0, 0, 0, 0.1)' : ''};
  &:hover {
    background-color: ${(props) =>
      props.isSelected && props.activeType === 'fill'
        ? '#883E89'
        : props.isSelected
        ? '#FFF'
        : '#FAFAFA'};
    box-shadow: 0px 1px 20px rgba(0, 0, 0, 0.1);
  }
  padding: ${(props) => props.padding ? props.padding : '24px'};

  justify-content:${(props) => props.justifyContent ? props.justifyContent : "unset"};
  @media screen and (min-width: 768px) {
    display: inline-flex;
    gap: 24px 24px;
    padding: ${(props) => props.padding ? props.padding : '24px'};
  }
  transition: all 0.2s linear;
`;

const RadioBtnHovering = css`
  &:hover{
    background-color:#FFF;
    box-shadow:0px 1px 20px rgba(0, 0, 0, 0.1);
    p,h4{
      color:#883E89;
    }
  }
`;

const RadioBtnHoveringAlt = css`
  &:hover{
    background-color:#fff;
    box-shadow:0px 4px 35px rgba(0, 0, 0, 0.08);
    p,h4{
      color:#783780;
    }
  }
`;

export interface RadioButtonProps {
  header?: string;
  text?: string;
  subText?: React.ReactNode;
  onChange: (value: string | number | undefined) => void;
  value: string | number | boolean | undefined;
  selected?: string | number | null;
  activeType?: 'outline' | 'fill';
  widthIcon?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  iconALign?: 'top' | 'bottom' | 'left' | 'right';
  iconActive?: string;
  iconInactive?: string;
  iconHover?: string;
  name?: string;
  padding?: string;
  width?: number;
  height?: number;
  justifyContent?: string;
  isHoverAlt?:boolean;
  borderRadius?: string;
  buttonGap?: string;
}

const RadioButton = ({
  name,
  activeType,
  textAlign,
  iconALign,
  header,
  text,
  subText,
  onChange,
  value,
  selected,
  iconActive,
  iconInactive,
  iconHover,
  padding,
  justifyContent,
  width,
  height,
  borderRadius,
  ...props
}: RadioButtonProps) => {
  const isSelected = selected === value;
  const [isHover,setIsHover] = useState<boolean>(false);
  return (
    <RadioButtonContainer
      className={isHover && props.isHoverAlt ? RadioBtnHoveringAlt : isHover ?  RadioBtnHovering: ""}
      name={name}
      activeType={activeType}
      iconALign={iconALign}
      header={header}
      text={text}
      width={width}
      height={height}
      onMouseOver={(e:any)=>{
        if(!isSelected)
        {
          setIsHover(true);
        }
      }}
      onMouseLeave={(e:any)=>{
        setIsHover(false);
      }}
      onClick={(e: any) =>{
        setIsHover(false);
        onChange({ ...e, target: { ...e.target, value, name } })
      }}
      isSelected={isSelected}
      padding={padding}
      justifyContent={justifyContent}
      borderRadius={borderRadius}
    >
      {props.widthIcon && (
        <Icon name={isSelected ? iconActive : (isHover ? iconHover : iconInactive)} />
      )}
      <RadioButtonDescription
        isSelected={isSelected}
        textAlign={textAlign}
        activeType={activeType}
      >
        {header && <h4>{header}</h4>}
        {text && <p>{text}</p>}
        {subText && <RadioButtonSubText>{subText}</RadioButtonSubText>}
      </RadioButtonDescription>
    </RadioButtonContainer>
  );
};

RadioButton.defaultProps = {
  text: '',
  selected: null,
  vaue: undefined,
  onChange: () => {},
};

export type RadioButtonGroupProps = Omit<
  RadioButtonProps,
  'header' | 'text' | 'onChange' | 'value' | 'selected'
> & {
  name?: string;
  children?: React.ReactNode;
  onChange?: (e: any) => void;
  value: string | number | null;
  theme?: ITheme;
};

const RadioButtonGroup = forwardRef<HTMLDivElement, RadioButtonGroupProps>(
  (
    { value, onChange, children, name, buttonGap, ...props }: RadioButtonGroupProps,
    ref
  ) => {
    const theme = useTheme();
    const [selected, setSelected] = useState<string | number | null>(value);
    const onChangeRadio = (event: any) => {
      const value = event.target.value;
      setSelected(value);
      if (onChange) {
        onChange(event);
      }
    };

    useEffect(() => {
      setSelected(value);
    }, [value]);

    return (
      <RadioButtonGroupContainer theme={theme} ref={ref} buttonGap={buttonGap}>
        {React.Children.toArray(children).map((item: any, index: number) => {
          return (
            item.type === RadioButton && (
              <RadioButton
                {...props}
                name={name}
                key={`aiva-${index}-radio`}
                header={item.props.header}
                text={item.props.text}
                subText={item.props.subText}
                value={item.props.value}
                selected={selected}
                onChange={onChangeRadio}
                iconActive={item.props.iconActive}
                iconInactive={item.props.iconInactive}
                iconHover={item.props.iconHover}
                padding={item.props.padding}
                justifyContent={item.props.justifyContent}
              />
            )
          );
        })}
      </RadioButtonGroupContainer>
    );
  }
);

RadioButtonGroup.defaultProps = {
  widthIcon: true,
  textAlign: 'left',
};
export { RadioButtonGroup, RadioButton };
